// FILE: src/components/admin/LockDialog.jsx
// Lock or unlock an account (WP-7, MTO 1.3.4, matrix 1.13).
//   PATCH /api/admin/users/:id/lock   { reason }  (required; 500 characters max)
//     400 CANNOT_LOCK_SELF · 409 ACCOUNT_ALREADY_LOCKED · 400 VALIDATION_FAILED
//   PATCH /api/admin/users/:id/unlock { reason? }
// Both answer { message, user } and are audited (USER_LOCKED / USER_UNLOCKED)
// with the reason. Cancel or Escape closes without a change; the reason field
// gets the focus (typing in it changes nothing).
import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Stack,
  TextField
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import IconDialogTitle from "../student/IconDialogTitle";
import fetchWithAuth from "../../utils/fetchWithAuth";
import { errorCode, errorMessage } from "../../i18n/errors";

export const REASON_MAX = 500;

/**
 * mode: "lock" | "unlock". onDone(result, { alreadyLocked }) runs after the
 * server's answer (also after ACCOUNT_ALREADY_LOCKED, so the page can reload
 * and show the lock someone else made).
 */
export default function LockDialog({ open, mode, user, name, onClose, onDone }) {
  const locking = mode === "lock";
  const [reason, setReason] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("");
      setFieldError("");
      setError(null);
      setBusy(false);
    }
  }, [open, mode]);

  const submit = async (e) => {
    e?.preventDefault();
    const text = reason.trim();
    if (locking && !text) {
      setFieldError("Say why you're locking the account. It's kept in the audit log.");
      return;
    }
    if (text.length > REASON_MAX) {
      setFieldError(`Use ${REASON_MAX} characters or fewer.`);
      return;
    }
    setFieldError("");
    setError(null);
    setBusy(true);
    try {
      const body = locking ? { reason: text } : text ? { reason: text } : {};
      const result = await fetchWithAuth(`/api/admin/users/${user._id}/${locking ? "lock" : "unlock"}`, {
        method: "PATCH",
        body: JSON.stringify(body)
      });
      setBusy(false);
      onDone(result, { alreadyLocked: false });
    } catch (err) {
      setBusy(false);
      setError(err || "INTERNAL_ERROR");
      if (errorCode(err) === "ACCOUNT_ALREADY_LOCKED") onDone(null, { alreadyLocked: true });
    }
  };

  const titleId = `${mode}-dialog-title`;
  const descId = `${mode}-dialog-description`;
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      aria-labelledby={titleId}
      aria-describedby={descId}
      fullWidth
      maxWidth="sm"
    >
      <IconDialogTitle id={titleId} icon={locking ? LockOutlinedIcon : LockOpenOutlinedIcon} tone={locking ? "error" : "primary"}>
        {locking ? `Lock ${name}'s account?` : `Unlock ${name}'s account?`}
      </IconDialogTitle>
      <DialogContent>
        <Stack spacing={2.5} component="form" id={`${mode}-form`} noValidate onSubmit={submit}>
          <DialogContentText id={descId} sx={{ color: "text.primary" }}>
            {locking
              ? "They're signed out at once and can't sign in until support unlocks the account. The reason is kept in the audit log and in their record."
              : "Unlock only after you've confirmed the person's identity (for example with their security questions over the phone). They can then sign in again; the failed-attempt count starts over."}
          </DialogContentText>
          {error && (
            <Alert severity="error" role="alert">
              {errorMessage(error)}
            </Alert>
          )}
          <TextField
            id={`${mode}-reason`}
            label={locking ? "Reason for locking" : "Note (optional)"}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (fieldError) setFieldError("");
            }}
            error={!!fieldError}
            helperText={
              fieldError ||
              (locking
                ? `For example: "Asked to by the student: lost phone". Up to ${REASON_MAX} characters.`
                : `For example: "Identity confirmed by phone". Up to ${REASON_MAX} characters.`)
            }
            required={locking}
            multiline
            minRows={3}
            autoFocus
            fullWidth
            disabled={busy}
            slotProps={{ htmlInput: { maxLength: REASON_MAX } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{ px: 3, pb: 2.5, flexWrap: "wrap", gap: 1, "& > :not(style) ~ :not(style)": { ml: 0 } }}
      >
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          type="submit"
          form={`${mode}-form`}
          variant="contained"
          color={locking ? "error" : "primary"}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} color="inherit" aria-hidden /> : locking ? <LockOutlinedIcon /> : <LockOpenOutlinedIcon />}
        >
          {busy ? (locking ? "Locking…" : "Unlocking…") : locking ? "Lock account" : "Unlock account"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
